import { FC, useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import ComparePage from '../compare-page';
import { fetchers } from '@pes-ui/utils';
import { Loader } from '../components/ui/Loader';
import { isHostnameContainsCC } from '../utils';
import type { ExtendedBaseRouteProps } from '../types';

const defaultMessages = {
    "comparison.label.priceWithVat": "Price inclusive of VAT",
    "product.endofCommercialisation.label": "Discontinued",
    "cart.addToCart.label": "Add To Cart",
    "BreadCrumbs.home.label": "Home",
    "all.badge.bestperformance": "Best Performance",
    "all.badge.bestseller": "Best Seller",
    "all.badge.bestvalue": "Best Value",
    "all.badge.featured": "Featured",
    "all.badge.new": "New",
    "comparator.display.text": "Compare Products",
    "comparison.label.clearList": "Clear the List",
    "comparison.label.copy": "Copy to Clipboard",
    "comparison.label.goToSearch": "Go to search",
    "comparison.label.seeCatalog":'See the catalog',
    "backToResults.label": "Back to results",
    "comparison.label.items": "Items",
    "comparison.label.linkCopied": "Link was copied to clipboard",
    "comparison.label.noItems": "No items to compare",
    "comparison.label.howToUse.part1": `Please, use checkboxes on the cards or special options on product pages to add items to the product comparison.`,
    "comparison.label.howToUse.part2": `to add items to the comparison`,
    "comparison.error.reachedMaxCartItems": "You have reached the maximum number of {maxCartItems} of items allowed in your cart. You can <goToCartLink>Go to Cart</goToCartLink> and edit your order.",
    "comparison.label.shareLink": "Share Link",
    "compare": "Compare Products",
    "compare.enterTotalLoad": "Enter the total load (power draw) of all devices to be protected by the UPS here. Based on the value entered we will estimate the run time that you will have during a total power outage. Adding run time increases availability by allowing the system to operate through longer outages without shutting down.",
    "compare.errorMessage": "Sorry, the following products could not be added to the comparison:",
    "comparison.error.general": "Your request could not be processed at this time. Please try again later, or <a href=\"https://www.apc.com/us/en/\">contact us</a> for support.",
    "comparison.label.hideSimilarities": "Hide Similarities",
    "comparison.label.highlightDiff": "Highlight Differences",
    "graph.runtime.time.h": "h",
    "compare.indicatesCapacity": "The maximum percentage of UPS capacity used to support your specified load.",
    "compare.maxCapacityUsed": "Max. Capacity Used",
    "compare.minutes": "m",
    "compare.pleaseEnterLoad": "Please enter load to see estimated run time",
    "compare.pleaseEnterLoadMaxCapacity": "Please enter load to see calculated Max. Capacity Used",
    "compare.runTime": "Run time for load",
    "compare.seconds": "s",
    "compare.totalLoadExceeds": "The total load (power draw) entered exceeds the maximum value for this product (?0)",
    "compare.watts": "W",
    "error.server": "Sorry, your request could not be processed. Please, try later.",
    "documents.label.availableVersions": "Available versions",
    "comparison.label.callForMoreInfo": "Call for More Information",
    "prp.price.label": "Price",
    "common.button.download": "Download",
    "common.button.puchaseCD": "Purchase CD",
    "search.label.productNotAvailableNew": "Not available in ?0",
    "prp.document.size.label": "Size",
    "software.download.fileTitle": "File title",
    "software.download.fileType": "File type",
    "software.osArch.32bit": "32-bit",
    "software.osArch.64bit": "64-bit",
    "software.updatedOn": "Updated on",
    "common.label.mva": "MVA",
    "common.label.mwatts": "MWatts",
    "common.label.kva": "kVA",
    "common.label.kwatts": "kWatts",
    "common.label.lbsSquareInch": "lbs./Square Inch",
    "comparison.label.addAllToCart": "Add all {n} to Cart",
    "comparison.label.itemsTotal": "Items:{n}",
    "comparison.label.addBothToCart": "Add both to Cart",
    "comparison.label.priceForAll": "Price for all {n} items",
    "comparison.label.priceForBoth": "Price for both items"
};

export const ComparePageWithIntl: FC<ExtendedBaseRouteProps> = ({
    contextRoot,
    countryCode,
    languageCode,
    bazaarVoiceEnabled,
    pageRoute,
    isPublic,
}) => {
    const [translationMessages, setTranslationMessages] = useState<Record<string, string>>(defaultMessages);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        fetchers.getTranslationsFetcher({
            contextRoot,
            countryCode: !isHostnameContainsCC() ? countryCode : null,
            languageCode,
        })
            .finally(() => {
                setIsLoading(false);
            })
            .then((response) => {
                setTranslationMessages(response.labels);
            });
    }, [contextRoot, countryCode, languageCode]);

    if (isLoading) {
        return <Loader />;
    }

    return (
        <IntlProvider
            defaultLocale='en'
            locale={languageCode ?? 'en'}
            messages={translationMessages}
        >
            <ComparePage
                contextRoot={contextRoot}
                countryCode={countryCode ?? 'us'}
                languageCode={languageCode ?? 'en'}
                bazaarVoiceEnabled={bazaarVoiceEnabled}
                pageRoute={pageRoute}
                isPublic={isPublic}
            />
        </IntlProvider>
    );
}
